import {
  Box,
  Button,
  Center,
  chakra,
  HStack,
  Link,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import {
  ArticleStatus,
  ArticleType,
  Author,
  createArticleEditRelativeUrl,
  createArticleRelativeUrl,
} from '../common/libs/article';
import { DiscloseScope } from '../common/libs/discloseScope';
import {
  AnalyticsEventProps,
  sendAnalyticsEvent,
} from '../common/libs/googleAnalytics';
import { createProfileImageUrl, createUserName } from '../common/libs/user';
import ArticleMetrics from './ArticleMetrics';
import { FaLock, HiCheckBadge } from './Icons';
import ArticleImage from './ArticleImage';

export type ArticleHeader = {
  articleId: string;
  name: string | null;
  articleType?: ArticleType | null;
  status: ArticleStatus | null;
  topImageM: string | null;
  sessionCount: number;
  likeCount: number; // スライド保存とページ保存の合算値
  commentCount: number;
  createDate: string; // ISO8601
  updateDate: string; // ISO8601
  firstPublishDate?: string | null; // ISO8601
  author: Author;
  discloseScope?: DiscloseScope | null;
};

export type Props = ArticleHeader & {
  layoutType?: 'horizontal' | 'vertical';
  editable?: boolean;
  badgeType?: BadgeType;
  gaEventLabel?: string;
  additionalGaEvent?: AnalyticsEventProps;
  rankingNumber?: number;
  className?: string;
  onShowThumbnail?: (articleId: string) => void;
  onClick?: (articleId: string) => void;
};

export type BadgeType = 'new' | 'updated';

function ArticleNameWithIcon({
  name,
  discloseScope = undefined,
}: {
  name: string | null;
  discloseScope?: DiscloseScope | null;
}) {
  const length = '0.875rem'; // same as fontSize of Text
  const lockStyleProps = {
    minWidth: length,
    minHeight: length,
  };

  return (
    <Box h="2.5em" display="inline-flex" gap={1}>
      {discloseScope && discloseScope === 'PARTIAL' && (
        <FaLock color="#D6BF51" style={lockStyleProps} />
      )}
      <Text
        h="2.5em"
        fontSize="sm" // '0.875rem' (chakra-ui default)
        fontWeight="bold"
        noOfLines={2}
        lineHeight="1.25"
        maxW="100%"
        wordBreak="break-all"
      >
        {name}
      </Text>
    </Box>
  );
}

/**
 * 投稿者のプロフィール画像
 *
 * プロフィール画像未設定または画像取得失敗時にはデフォルト画像を表示する。
 */
export function AuthorProfileImage({
  author,
  size = 8,
}: {
  author: ArticleHeader['author'];
  size?: number;
}) {
  let imageUrl = '';
  if (!author.profilePhotoS3Key && !author.idBaseProfileUrl) {
    imageUrl = '/images/avatar_default_gray.png';
  } else {
    imageUrl = createProfileImageUrl(author.userId, {
      version: author.updateDateEpochMilliseconds,
    });
    if (author.idBaseProfileUrl) {
      imageUrl += `&idurl=${encodeURIComponent(author.idBaseProfileUrl)}`;
    }
  }
  const fullName = createUserName(author);
  return (
    <Box pos="relative" w={size} h={size} borderRadius="full" overflow="hidden">
      <NextImage
        src={imageUrl}
        alt={fullName}
        layout="fill"
        objectFit="cover"
      />
    </Box>
  );
}

export function AuthorName({
  name,
  isOfficial,
  bg = undefined,
}: {
  name: string | null;
  isOfficial?: boolean;
  bg?: string;
}) {
  return (
    <HStack align={'flex-start'} maxW={'85%'}>
      <Text fontSize="sm" noOfLines={1} lineHeight={1.3} bg={bg}>
        {name}
      </Text>
      {isOfficial && (
        <Box ml={1}>
          <HiCheckBadge size={18} color="#008e14" />
        </Box>
      )}
    </HStack>
  );
}

/**
 * バッジ表示
 */
export const ArticleBadge = chakra(
  ({ badgeType, className }: { badgeType: BadgeType; className?: string }) => {
    let text;
    switch (badgeType) {
      case 'new':
        text = 'NEW';
        break;
      case 'updated':
        text = '更新';
        break;
    }

    return (
      <Box
        className={className}
        display="inline-block"
        color="white"
        fontSize="x-small"
        fontWeight="bold"
        bg="#e26044"
        borderRadius="full"
        px={3}
        py={1}
      >
        {text}
      </Box>
    );
  },
);

/**
 * 編集リンク
 */
function ArticleEditLink({ articleId }: { articleId: string }) {
  const articleEditUrl = createArticleEditRelativeUrl(articleId);
  return (
    <NextLink href={articleEditUrl} passHref>
      <Button size="xs" variant="outline" p={2}>
        編集
      </Button>
    </NextLink>
  );
}

function ArticleRankingNumber({ rankingNumber }: { rankingNumber: number }) {
  const bgGradient = [
    'linear(to-r, yellow.400, yellow.500)',
    'linear(to-r, gray.300, gray.400)',
    'linear(to-r, yellow.700, yellow.800)',
    'gray',
  ];
  return (
    <Center
      pos="absolute"
      top={0}
      left={0}
      bg="gray.500"
      bgGradient={bgGradient[rankingNumber - 1]}
      borderRadius="full"
      color="white"
      fontSize="xs"
      w={6}
      h={6}
    >
      {rankingNumber}
    </Center>
  );
}

/**
 * ArticleSummaryコンポーネント（縦方向表示）
 *
 * 上側にスライド画像、下側にスライド情報が表示される。
 */
function ArticleSummaryVertical({
  articleId,
  name,
  articleType,
  status,
  topImageM,
  sessionCount,
  likeCount,
  commentCount,
  editable = false,
  updateDate,
  author,
  badgeType,
  discloseScope = undefined,
  onShowThumbnail,
}: Props) {
  // TODO 今後isOfficialフラグをDBに持つ
  const isOfficial = author.isOfficialAccount ?? false;

  const ref = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState(186);
  const [metaDataHeight, setMetaDataHeight] = useState(124);

  // サムネイル画像divの縦横比が常に16:9になるよう調整
  const handleResize = () => {
    if (ref.current?.offsetWidth) {
      const wrapperWidth = ref.current.offsetWidth;
      const thumbnailHeight = Math.floor((wrapperWidth / 16) * 9);
      // サムネイルサイズが大きくなりすぎないよう200pxを最大値とする
      setThumbHeight(thumbnailHeight < 200 ? thumbnailHeight : 200);
      // 更新日が折り返して表示される場合に下のpaddingが足りなくなる問題を回避するため3カラムの時の最大幅を基準に高さを変更
      setMetaDataHeight(wrapperWidth < 330 ? 136 : 124);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box pos="relative">
      <Center
        ref={ref}
        pos="relative"
        h={thumbHeight}
        bg="blackAlpha.300"
        borderBottomColor="gray.50"
        borderBottomWidth={1}
        overflow="hidden"
      >
        <ArticleImage
          articleId={articleId}
          articleType={articleType}
          status={status}
          topImageM={topImageM}
          updateDate={updateDate}
          alt={name}
          onShowThumbnail={onShowThumbnail}
        />
      </Center>
      <VStack
        align="stretch"
        spacing={1}
        overflowX="visible"
        px={3}
        py={2}
        h={metaDataHeight}
      >
        <ArticleNameWithIcon name={name} discloseScope={discloseScope} />
        <HStack spacing={2} py={1} h="3em">
          <AuthorProfileImage author={author} />
          <AuthorName name={createUserName(author)} isOfficial={isOfficial} />
        </HStack>
        <ArticleMetrics
          likeCount={likeCount}
          sessionCount={sessionCount}
          updateDateIsoString={updateDate}
          viewType="list"
          articleType={articleType}
        />
      </VStack>
      {editable && (
        <Box pos="absolute" right={2} bottom={2}>
          <ArticleEditLink articleId={articleId} />
        </Box>
      )}
      {!!badgeType && (
        <ArticleBadge badgeType={badgeType} pos="absolute" top={2} right={2} />
      )}
    </Box>
  );
}

/**
 * ArticleSummaryコンポーネント（横方向表示）
 *
 * 左側にスライド画像、右側にスライド情報が表示される。
 */
function ArticleSummaryHorizontal({
  articleId,
  name,
  articleType,
  status,
  topImageM,
  sessionCount,
  likeCount,
  commentCount,
  editable = false,
  updateDate,
  author,
  badgeType,
  rankingNumber,
  discloseScope = undefined,
  onShowThumbnail,
}: Props) {
  return (
    <HStack align="flex-start" h={110} p={2} spacing={2} pos="relative">
      <Center
        flex="0 0 120px"
        h={90}
        bg="blackAlpha.300"
        borderWidth={1}
        borderColor="gray.50"
        borderRadius="md"
        overflow="hidden"
        pos="relative"
        order={{
          base: 2,
          md: 1,
        }}
      >
        <ArticleImage
          articleId={articleId}
          articleType={articleType}
          status={status}
          topImageM={topImageM}
          updateDate={updateDate}
          alt={name}
          onShowThumbnail={onShowThumbnail}
        />
        {!!badgeType && (
          <ArticleBadge
            badgeType={badgeType}
            pos="absolute"
            top={1}
            right={1}
          />
        )}
      </Center>
      <VStack
        flex="1"
        minW={0}
        h="full"
        align="stretch"
        spacing={0}
        overflowX="visible"
        py={0.5}
        order={{
          base: 1,
          md: 2,
        }}
      >
        <Box h="2.6em" pl={rankingNumber ? 5 : 0}>
          <ArticleNameWithIcon name={name} discloseScope={discloseScope} />
        </Box>
        <Spacer />
        <HStack align="center" spacing={0}>
          <ArticleMetrics
            author={author}
            likeCount={likeCount}
            sessionCount={sessionCount}
            updateDateIsoString={updateDate}
            viewType="list-mobile"
            articleType={articleType}
          />
          <Spacer />
          {editable && <ArticleEditLink articleId={articleId} />}
        </HStack>
      </VStack>
    </HStack>
  );
}

function ArticleSummary(props: Props) {
  const { layoutType, className, rankingNumber } = props;

  return (
    <Box
      boxShadow="0 0 8px rgba(0,0,0,0.08)"
      borderRadius="lg"
      overflow="hidden"
      className={className}
      pos="relative"
    >
      {layoutType === 'vertical' ? (
        <ArticleSummaryVertical {...props} />
      ) : (
        <ArticleSummaryHorizontal {...props} />
      )}
      {rankingNumber && <ArticleRankingNumber rankingNumber={rankingNumber} />}
    </Box>
  );
}

/**
 * リンク化されたArticleSummaryコンポーネント
 *
 * GAイベント情報が指定されていた場合、リンク押下時にGAイベントを送信する。
 */
function LinkifiedArticleSummary(props: Props) {
  const { articleId, gaEventLabel, additionalGaEvent, onClick } = props;
  const articleUrl = createArticleRelativeUrl(articleId);

  return (
    <NextLink href={articleUrl} passHref>
      <Link
        display="inline-block"
        color="inherit"
        _hover={{
          textDecoration: 'none',
        }}
        onClick={() => {
          gaEventLabel && sendAnalyticsEvent('slide', 'click', gaEventLabel);
          additionalGaEvent &&
            sendAnalyticsEvent(
              additionalGaEvent.category,
              additionalGaEvent.action,
              additionalGaEvent.label,
            );
          onClick?.(articleId);
        }}
      >
        <ArticleSummary {...props} />
      </Link>
    </NextLink>
  );
}

/**
 * メモ化されたArticleSummaryコンポーネント
 *
 * 不必要な再renderを防いでパフォーマンス向上を見込む。
 * 以下のプロパティのいずれかが変化した場合のみ再renderが必要だと判断する。
 * - articleId
 * - layoutType
 */
const MemoizedArticleSummary = React.memo(
  LinkifiedArticleSummary,
  (prevProps, nextProps) => {
    return (
      prevProps.articleId === nextProps.articleId &&
      prevProps.layoutType === nextProps.layoutType
    );
  },
);

export default chakra(MemoizedArticleSummary);
